const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';

// API helper with Auth0 token (for protected endpoints)
const apiRequest = async (endpoint, options = {}, getAccessTokenSilently) => {
  try {
    // Get Auth0 access token
    const token = await getAccessTokenSilently({
      authorizationParams: {
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
      }
    });
    const defaultHeaders = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };

    const config = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers
      }
    };

    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'API request failed');
    }

    return data;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

// Public API helper (no authentication required)
const publicRequest = async (endpoint, options = {}) => {
  try {
    const defaultHeaders = {
      'Content-Type': 'application/json'
    };

    const config = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers
      }
    };

    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || data.message || 'API request failed');
    }

    return data;
  } catch (error) {
    console.error('Public API Error:', error);
    throw error;
  }
};

// Booking Template API
export const bookingTemplateAPI = {

  saveTemplate: async (templateData, getAccessTokenSilently) => {
    return await apiRequest('/booking/templates', {
      method: 'POST',
      body: JSON.stringify(templateData)
    }, getAccessTokenSilently);
  },

  updateTemplate: async (templateId, templateData, getAccessTokenSilently) => {
    return await apiRequest(`/booking/templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(templateData)
    }, getAccessTokenSilently);
  },

  getTemplate: async (templateId, getAccessTokenSilently) => {
    return await apiRequest(`/booking/templates/${templateId}`, {
      method: 'GET'
    }, getAccessTokenSilently);
  },

  getUserTemplates: async (getAccessTokenSilently) => {
    return await apiRequest('/booking/templates', {
      method: 'GET'
    }, getAccessTokenSilently);
  },

  deleteTemplate: async (templateId, getAccessTokenSilently) => {
    return await apiRequest(`/booking/templates/${templateId}`, {
      method: 'DELETE'
    }, getAccessTokenSilently);
  },

  getProfile: async (getAccessTokenSilently) => {
    return await apiRequest('/booking/profile', {
      method: 'GET'
    }, getAccessTokenSilently);
  }
};

// Invitation API (Protected - ServiceProvider only)
export const invitationAPI = {

  /**
   * Send invitation to a student
   * @param {Object} invitationData - { type: 'email'|'whatsapp', contact: string }
   * @param {Function} getAccessTokenSilently - Auth0 token function
   * @returns {Promise<Object>} { success: true, data: { invitationId, email, status, expiresAt } }
   */
  sendInvitation: async (invitationData, getAccessTokenSilently) => {
    return await apiRequest('/invitations/send', {
      method: 'POST',
      body: JSON.stringify(invitationData)
    }, getAccessTokenSilently);
  },

  /**
   * Get all invitations for the current instructor
   * @param {Function} getAccessTokenSilently - Auth0 token function
   * @returns {Promise<Object>} { success: true, data: [...invitations] }
   */
  getInvitations: async (getAccessTokenSilently) => {
    return await apiRequest('/invitations', {
      method: 'GET'
    }, getAccessTokenSilently);
  },

  /**
   * Get a single invitation by ID
   * @param {string} invitationId - Invitation ID
   * @param {Function} getAccessTokenSilently - Auth0 token function
   * @returns {Promise<Object>} { success: true, data: {...invitation} }
   */
  getInvitation: async (invitationId, getAccessTokenSilently) => {
    return await apiRequest(`/invitations/${invitationId}`, {
      method: 'GET'
    }, getAccessTokenSilently);
  },

  /**
   * Delete a student invitation
   * @param {string} invitationId - Invitation ID
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  deleteInvitation: async (invitationId, getAccessTokenSilently) => {
    return await apiRequest(`/invitations/${invitationId}`, {
      method: 'DELETE'
    }, getAccessTokenSilently);
  }
};

// Public API (No authentication required)
export const publicAPI = {

  /**
   * Validate student invitation token
   * @param {string} token - Invitation token from URL
   * @returns {Promise<Object>} { success: true, data: { invitationId, email, instructorId, expiresAt, valid } }
   */
  validateToken: async (token) => {
    return await publicRequest(`/students/validate-token/${token}`, {
      method: 'GET'
    });
  },

  /**
   * Complete student signup
   * @param {Object} studentData - Student registration data
   * @returns {Promise<Object>} { success: true, data: { studentId, email, name, auth0UserId } }
   */
  studentSignup: async (studentData) => {
    return await publicRequest('/students/signup', {
      method: 'POST',
      body: JSON.stringify(studentData)
    });
  },

  /**
   * Validate instructor invitation token
   * @param {string} token - Invitation token from URL
   * @returns {Promise<Object>} { success: true, data: { invitationId, email, expiresAt, valid } }
   */
  validateInstructorToken: async (token) => {
    return await publicRequest(`/instructors/validate-token/${token}`, {
      method: 'GET'
    });
  },

  /**
   * Complete instructor signup
   * @param {Object} instructorData - Instructor registration data
   * @returns {Promise<Object>} { success: true, data: { instructorId, email, name, auth0UserId } }
   */
  instructorSignup: async (instructorData) => {
    return await publicRequest('/instructors/signup', {
      method: 'POST',
      body: JSON.stringify(instructorData)
    });
  }
};

/**
 * Student API
 * For managing student data
 */
export const studentAPI = {
  /**
   * Get all students
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  getAllStudents: async (getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/manageStudents`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch students');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching students:', error);
      throw error;
    }
  },

  /**
   * Get students by status
   * @param {string} status - Student status ('active', 'approve', 'inactive', 'passed', 'deleted')
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  getStudentsByStatus: async (status, getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/manageStudents/status/${status}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch students');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching students by status:', error);
      throw error;
    }
  },

  /**
   * Search students
   * @param {string} searchTerm - Search term
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  searchStudents: async (searchTerm, getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/manageStudents/search?q=${encodeURIComponent(searchTerm)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to search students');
      }

      return await response.json();
    } catch (error) {
      console.error('Error searching students:', error);
      throw error;
    }
  },

  /**
   * Get student by ID
   * @param {string} studentId - Student ID
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  getStudentById: async (studentId, getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/manageStudents/${studentId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch student');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching student:', error);
      throw error;
    }
  },

  /**
   * Update student status
   * @param {string} studentId - Student ID
   * @param {string} status - New status
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  updateStudentStatus: async (studentId, status, getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/manageStudents/${studentId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update student status');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating student status:', error);
      throw error;
    }
  },

  /**
   * Delete student
   * @param {string} studentId - Student ID
   * @param {boolean} permanent - Whether to permanently delete (default: false)
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  deleteStudent: async (studentId, permanent = false, getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const url = permanent
        ? `${API_BASE_URL}/manageStudents/${studentId}?permanent=true`
        : `${API_BASE_URL}/manageStudents/${studentId}`;

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete student');
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting student:', error);
      throw error;
    }
  },

  /**
 * Get complete student details from all tables
 * @param {string} studentId - Student ID
 * @param {Function} getAccessTokenSilently - Auth0 token function
 */
  getCompleteStudentDetails: async (studentId, getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/manageStudents/${studentId}/complete`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch student details');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching complete student details:', error);
      throw error;
    }
  },

  /**
   * Update student limited fields (status, phone, addresses)
   * @param {string} studentId - Student ID
   * @param {Object} updates - Fields to update
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  updateStudentFields: async (studentId, updates, getAccessTokenSilently) => {
    try {
      console.log('Updating student:', studentId, 'with data:', updates);
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/manageStudents/${studentId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update student');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating student:', error);
      throw error;
    }
  },
  /**
   * Get all students registered under the authenticated instructor
   */
  getMyStudents: async (getAccessTokenSilently) => {
    try {
      console.log('Fetching my students');
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/manageStudents/my-students`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch all students registered under the authenticated instructor');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching students:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch students',
      };
    }
  }

};

// Booking Slot API
export const bookingSlotAPI = {
  /**
   * Publish a template - Generate and save slots to BookingSlot table
   */
  publishTemplate: async (templateId, getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/booking-slots/publish/${templateId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to publish template');
      }

      return await response.json();
    } catch (error) {
      console.error('Error publishing template:', error);
      throw error;
    }
  },


  /**
 * Get my instructor's published slots (based on UserProviderRelationships)
 */
  getMyInstructorSlots: async (getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/booking-slots/my-instructor-slots`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch instructor schedule');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching instructor schedule:', error);
      throw error;
    }
  },
  /**
   * Get version history for the authenticated user
   */
  getVersionHistory: async (getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/booking-slots/versions`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch version history');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching version history:', error);
      throw error;
    }
  },

  /**
   * Get slots for a specific date
   */
  getSlotsByDate: async (date, instructorId, getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/booking-slots/slots/${date}?instructorId=${instructorId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch slots');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching slots:', error);
      throw error;
    }
  },

  /**
   * Get available slots for booking
   */
  getAvailableSlots: async (instructorId, dateFrom, dateTo, getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const url = new URL(`${API_BASE_URL}/booking-slots/available`);
      url.searchParams.append('instructorId', instructorId);
      if (dateFrom) url.searchParams.append('dateFrom', dateFrom);
      if (dateTo) url.searchParams.append('dateTo', dateTo);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch available slots');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching available slots:', error);
      throw error;
    }
  },
  /**
   * Get the currently logged-in instructor's published template
   * Used by ServiceProvider to view their own published slots
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  getPublishedTemplate: async (getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/booking-slots/my-published-template`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch published template');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching published template:', error);
      throw error;
    }
  },

/**
 * Get the currently published template for the logged-in instructor
 * Used for comparison with active template before publishing
 */
  getCurrentPublishedTemplate: async (getAccessTokenSilently) => {
    const token = await getAccessTokenSilently();
    const response = await fetch(`${API_BASE_URL}/booking-slots/published-template`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        // No published template exists yet (first publish)
        return { success: false, message: 'No published template found' };
      }
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch published template');
    }

    return await response.json();
  },

/**
 * Validate if template can be published without booking conflicts
 * Checks existing bookings against template changes
 */
validatePublish: async (templateId, getAccessTokenSilently) => {
  try {
    const token = await getAccessTokenSilently();
    const response = await fetch(`${API_BASE_URL}/booking-slots/validate-publish/${templateId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to validate template publish');
    }

    return await response.json();
  } catch (error) {
    console.error('Error validating template publish:', error);
    throw error;
  }
},

};

/**
 * Booking API
 * For creating and managing slot bookings
 */
export const bookingAPI = {
  /**
   * Create a single booking
   * @param {Object} bookingData - { date, startTime, endTime, providerId, providerPhone }
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  createBooking: async (bookingData, getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(bookingData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create booking');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating booking:', error);
      throw error;
    }
  },

  /**
   * Create multiple bookings at once
   * @param {Object} data - { slots: [], providerId, providerPhone }
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  createMultipleBookings: async (data, getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/bookings/multiple`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create bookings');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating multiple bookings:', error);
      throw error;
    }
  },

  /**
   * Get all bookings for the current student
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  getMyBookings: async (getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/bookings/my-bookings`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch bookings');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching my bookings:', error);
      throw error;
    }
  },

  /**
   * Get all bookings for the current instructor
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  getInstructorBookings: async (getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/bookings/instructor-bookings`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch instructor bookings');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching instructor bookings:', error);
      throw error;
    }
  },

  /**
   * NEW: Get all bookings for a specific instructor by phone number
   * This uses the new booking ID structure (instructor phone is first 10 digits)
   * Used to check which slots are already booked
   * @param {string} instructorPhone - Instructor's 10-digit phone number
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  getBookingsByInstructorPhone: async (instructorPhone, getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/bookings/by-instructor-phone/${instructorPhone}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch bookings');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching bookings by instructor phone:', error);
      throw error;
    }
  },

  /**
   * Get a specific booking by ID
   * @param {string} bookingId - Booking ID (34-digit identifier)
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  getBooking: async (bookingId, getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/bookings/${bookingId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch booking');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching booking:', error);
      throw error;
    }
  },

  /**
   * Cancel a booking
   * @param {string} bookingId - Booking ID to cancel
   * @param {string} reason - Optional cancellation reason
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  cancelBooking: async (bookingId, reason, getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/bookings/${bookingId}/cancel`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ reason })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to cancel booking');
      }

      return await response.json();
    } catch (error) {
      console.error('Error cancelling booking:', error);
      throw error;
    }
  },
  /**
   * Create multiple bookings on behalf of a student (instructor booking for student)
   * @param {Object} data - { studentId, slots, providerId, providerPhone }
   */
  createMultipleBookingsForStudent: async (data, getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${API_BASE_URL}/bookings/book-for-student`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(data)
        }
      );
      // Parse JSON response
      const responseData = await response.json();

      // Check if request was successful
      if (!response.ok) {
        return {
          success: false,
          error: responseData.error || 'Failed to create bookings for student',
          data: responseData.data || {
            successCount: 0,
            failCount: 0,
            succeeded: [],
            failed: []
          }
        };
      }

      return responseData;
    } catch (error) {
      console.error('Error booking for student:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create bookings for student',
      };
    }
  },

  /**
   * Get today's and tomorrow's bookings for the authenticated instructor,
   * enriched with student pickup address.
   * @param {Function} getAccessTokenSilently - Auth0 token function
   * @returns {Promise<Object>} { today: [...], tomorrow: [...], todayDate, tomorrowDate }
   */
  getDashboardBookings: async (getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/bookings/dashboard-bookings`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch dashboard bookings');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching dashboard bookings:', error);
      throw error;
    }
  },

/**
 * Get all progress entries for the authenticated student
 */
  getMyProgress: async (getAccessTokenSilently) => {
    return await apiRequest('/progress-tracker/my-progress', { method: 'GET' }, getAccessTokenSilently);
  }
};

/**
 * Cancellation API
 * For managing booking cancellation requests
 */
export const cancellationAPI = {
  /**
   * Create a single cancellation request
   * @param {Object} cancellationData - Cancellation details
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  createCancellationRequest: async (cancellationData, getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/cancellations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(cancellationData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create cancellation request');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating cancellation request:', error);
      throw error;
    }
  },

  /**
   * Create multiple cancellation requests at once
   * @param {Object} data - { cancellations: [] }
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  createMultipleCancellationRequests: async (data, getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/cancellations/multiple`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create cancellation requests');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating multiple cancellation requests:', error);
      throw error;
    }
  },

  /**
   * Get student's cancellation requests
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  getMyCancellationRequests: async (getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/cancellations/my-requests`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch cancellation requests');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching my cancellation requests:', error);
      throw error;
    }
  },

  /**
   * Get instructor's cancellation requests
   * @param {string} status - Optional status filter ('requested', 'cancelled', 'denied')
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  getInstructorCancellationRequests: async (status, getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const url = status
        ? `${API_BASE_URL}/cancellations/instructor-requests?status=${status}`
        : `${API_BASE_URL}/cancellations/instructor-requests`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch cancellation requests');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching instructor cancellation requests:', error);
      throw error;
    }
  },

  /**
   * Approve a single cancellation request
   * @param {string} cancellationId - Cancellation ID to approve
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  approveCancellationRequest: async (cancellationId, getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/cancellations/approve/${cancellationId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to approve cancellation');
      }

      return await response.json();
    } catch (error) {
      console.error('Error approving cancellation:', error);
      throw error;
    }
  },

  /**
   * Approve multiple cancellation requests
   * @param {Object} data - { cancellationIds: [] }
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  approveMultipleCancellations: async (data, getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/cancellations/approve-multiple`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to approve cancellations');
      }

      return await response.json();
    } catch (error) {
      console.error('Error approving multiple cancellations:', error);
      throw error;
    }
  },

  /**
   * Deny a cancellation request
   * @param {string} cancellationId - Cancellation ID to deny
   * @param {Object} data - { reason: string }
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  denyCancellationRequest: async (cancellationId, data, getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/cancellations/deny/${cancellationId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to deny cancellation');
      }

      return await response.json();
    } catch (error) {
      console.error('Error denying cancellation:', error);
      throw error;
    }
  },


  /**
   * Create multiple cancellation requests initiated by instructor
   * This allows instructors to create cancellation requests on behalf of students
   * @param {Object} data - { cancellations: [] }
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  createInstructorCancellationRequests: async (data, getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/cancellations/instructor-multiple`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create instructor cancellation requests');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating instructor cancellation requests:', error);
      throw error;
    }
  },

  /**
   * Get a specific cancellation request by ID
   * @param {string} cancellationId - Cancellation ID
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  getCancellationRequest: async (cancellationId, getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/cancellations/${cancellationId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch cancellation request');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching cancellation request:', error);
      throw error;
    }
  }
};


/**
 * Instructor API
 * For managing instructor data (Admin only)
 */
export const adminAPI = {
  /**
   * Get all instructors
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  getAllInstructors: async (getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/admin/all`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch instructors');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching instructors:', error);
      throw error;
    }
  },

  /**
   * Get instructors by status
   * @param {string} status - Instructor status ('active', 'inactive', 'suspended')
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  getInstructorsByStatus: async (status, getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/admin/status/${status}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch instructors');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching instructors by status:', error);
      throw error;
    }
  },

  /**
   * Get pending instructor invitations
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  getPendingInvitations: async (getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/admin/invitations/pending`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch pending invitations');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching pending invitations:', error);
      throw error;
    }
  },

  /**
   * Get single instructor by ID
   * @param {string} instructorId - Instructor ID
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  getInstructor: async (instructorId, getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/admin/${instructorId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch instructor');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching instructor:', error);
      throw error;
    }
  },


  /**
   * Delete an instructor invitation
   * @param {string} invitationId - Invitation ID
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  deleteInvitation: async (invitationId, getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/admin/invitations/${invitationId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete invitation');
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting instructor invitation:', error);
      throw error;
    }
  },

  /**
   * Update instructor status
   * @param {string} instructorId - Instructor ID
   * @param {string} status - New status ('active', 'inactive', 'suspended')
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  updateInstructorStatus: async (instructorId, status, getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/admin/${instructorId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update instructor status');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating instructor status:', error);
      throw error;
    }
  }
};

/**
 * Instructor Configuration API
 * Manage instructor-specific configuration settings
 */
export const instructorConfigAPI = {
  /**
   * Get instructor configuration
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  getConfiguration: async (getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/instructor-config`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch configuration');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching instructor configuration:', error);
      throw error;
    }
  },

  /**
   * Update instructor configuration
   * @param {Object} configData - Configuration data
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  updateConfiguration: async (configData, getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/instructor-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(configData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update configuration');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating instructor configuration:', error);
      throw error;
    }
  },

  /**
   * Create default configuration
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  createDefaultConfiguration: async (getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/instructor-config/default`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create default configuration');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating default configuration:', error);
      throw error;
    }
  },

  /**
   * Get default configuration values
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  getDefaults: async (getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/instructor-config/defaults`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch defaults');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching default configuration:', error);
      throw error;
    }
  }
};

// Tracker Template API

// ── Tracker Default Templates API (global library, admin-managed) ─────────────
export const trackerDefaultTemplateAPI = {

  /**
   * Get all default templates — readable by any ServiceProvider / admin
   */
  getAll: async (getAccessTokenSilently) => {
    const token = await getAccessTokenSilently();
    const response = await fetch(`${API_BASE_URL}/tracker-default-templates`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch default templates');
    }
    return response.json();
  },

  /**
   * Create a default template — admin only
   * @param {Object} data - { heading, items: [{ item, type }], category? }
   */
  create: async (data, getAccessTokenSilently) => {
    const token = await getAccessTokenSilently();
    const response = await fetch(`${API_BASE_URL}/tracker-default-templates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create default template');
    }
    return response.json();
  },

  /**
   * Update a default template — admin only
   * @param {string} defaultTemplateId
   * @param {Object} data - { heading?, items?, category? }
   */
  update: async (defaultTemplateId, data, getAccessTokenSilently) => {
    const token = await getAccessTokenSilently();
    const response = await fetch(`${API_BASE_URL}/tracker-default-templates/${defaultTemplateId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update default template');
    }
    return response.json();
  },

  /**
   * Delete a default template — admin only
   * @param {string} defaultTemplateId
   */
  delete: async (defaultTemplateId, getAccessTokenSilently) => {
    const token = await getAccessTokenSilently();
    const response = await fetch(`${API_BASE_URL}/tracker-default-templates/${defaultTemplateId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete default template');
    }
    return response.json();
  }

};

export const trackerTemplateAPI = {

  /**
   * Get all tracker template entries for the logged-in instructor
   */
  getTemplates: async (getAccessTokenSilently) => {
    const token = await getAccessTokenSilently();
    const response = await fetch(`${API_BASE_URL}/tracker-templates`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch tracker templates');
    }
    return response.json();
  },

  /**
   * Create a new tracker template entry
   * @param {Object} data - { heading, item, type }
   */
  createTemplate: async (data, getAccessTokenSilently) => {
    const token = await getAccessTokenSilently();
    const response = await fetch(`${API_BASE_URL}/tracker-templates`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create tracker template');
    }
    return response.json();
  },

  /**
   * Update a tracker template entry
   * @param {string} templateId
   * @param {Object} data - { heading, item, type }
   */
  updateTemplate: async (templateId, data, getAccessTokenSilently) => {
    const token = await getAccessTokenSilently();
    const response = await fetch(`${API_BASE_URL}/tracker-templates/${templateId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update tracker template');
    }
    return response.json();
  },

  /**
   * Delete a tracker template entry
   * @param {string} templateId
   */
  deleteTemplate: async (templateId, getAccessTokenSilently) => {
    const token = await getAccessTokenSilently();
    const response = await fetch(`${API_BASE_URL}/tracker-templates/${templateId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete tracker template');
    }
    return response.json();
  }

};

// Tracker Levels API (published configurations)
export const trackerLevelsAPI = {

  /**
   * Publish a fully-expanded configuration to the TrackerLevels table
   * @param {Object} data - { sourceConfigId, levels: [{ level, templates: [{ templateId, heading, items }] }] }
   */
  publish: async (data, getAccessTokenSilently) => {
    const token = await getAccessTokenSilently();
    const response = await fetch(`${API_BASE_URL}/tracker-levels`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to publish configuration');
    }
    return response.json();
  },

  /**
   * Get all published records for the logged-in instructor
   */
  getAll: async (getAccessTokenSilently) => {
    const token = await getAccessTokenSilently();
    const response = await fetch(`${API_BASE_URL}/tracker-levels`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch published records');
    }
    return response.json();
  },

  /**
   * Update an existing published record
   * @param {string} trackerLevelId
   * @param {Object} data - { sourceConfigId, levels }
   */
  updateRecord: async (trackerLevelId, data, getAccessTokenSilently) => {
    const token = await getAccessTokenSilently();
    const response = await fetch(`${API_BASE_URL}/tracker-levels/${trackerLevelId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update published record');
    }
    return response.json();
  },

  /**
   * Get a single published record by ID
   * @param {string} trackerLevelId
   */
  getById: async (trackerLevelId, getAccessTokenSilently) => {
    const token = await getAccessTokenSilently();
    const response = await fetch(`${API_BASE_URL}/tracker-levels/${trackerLevelId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch record');
    }
    return response.json();
  },

  /**
   * Delete a published record
   * @param {string} trackerLevelId
   */
  deleteRecord: async (trackerLevelId, getAccessTokenSilently) => {
    const token = await getAccessTokenSilently();
    const response = await fetch(`${API_BASE_URL}/tracker-levels/${trackerLevelId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete record');
    }
    return response.json();
  }

};

// Tracker Levels Template API
export const trackerLevelsTemplateAPI = {

  /**
   * Get all level configurations for the logged-in instructor
   */
  getConfigs: async (getAccessTokenSilently) => {
    const token = await getAccessTokenSilently();
    const response = await fetch(`${API_BASE_URL}/tracker-levels-templates`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch level configurations');
    }
    return response.json();
  },

  /**
   * Save a new level configuration
   * @param {Object} data - { levels: [{ level, templates: [{ templateId, heading }] }] }
   */
  createConfig: async (data, getAccessTokenSilently) => {
    const token = await getAccessTokenSilently();
    const response = await fetch(`${API_BASE_URL}/tracker-levels-templates`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to save level configuration');
    }
    return response.json();
  },

  /**
   * Update an existing level configuration
   * @param {string} configId
   * @param {Object} data - { levels: [{ level, templates: [{ templateId, heading }] }] }
   */
  updateConfig: async (configId, data, getAccessTokenSilently) => {
    const token = await getAccessTokenSilently();
    const response = await fetch(`${API_BASE_URL}/tracker-levels-templates/${configId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update level configuration');
    }
    return response.json();
  },

  /**
   * Get a single configuration by ID
   * @param {string} configId
   */
  getConfigById: async (configId, getAccessTokenSilently) => {
    const token = await getAccessTokenSilently();
    const response = await fetch(`${API_BASE_URL}/tracker-levels-templates/${configId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch configuration');
    }
    return response.json();
  },

  /**
   * Delete a configuration
   * @param {string} configId
   */
  deleteConfig: async (configId, getAccessTokenSilently) => {
    const token = await getAccessTokenSilently();
    const response = await fetch(`${API_BASE_URL}/tracker-levels-templates/${configId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete configuration');
    }
    return response.json();
  }

};

// Progress Tracker API
export const progressTrackerAPI = {

  /**
   * Create a new progress entry for a lesson session
   * @param {Object} data - { studentId, progressLevel, trackerLevelId, entries }
   */
  createProgress: async (data, getAccessTokenSilently) => {
    const token = await getAccessTokenSilently();
    const response = await fetch(`${API_BASE_URL}/progress-tracker`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create progress entry');
    }
    return response.json();
  },

  /**
   * Update an existing draft (save or submit)
   * @param {string} progressId
   * @param {Object} data - { entries, status: 'draft' | 'submitted' }
   */
  updateProgress: async (progressId, data, getAccessTokenSilently) => {
    const token = await getAccessTokenSilently();
    const response = await fetch(`${API_BASE_URL}/progress-tracker/${progressId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update progress entry');
    }
    return response.json();
  },

  /**
   * Get all progress entries for a student (draft first, then submitted)
   * @param {string} studentId
   */
  getProgressByStudent: async (studentId, getAccessTokenSilently) => {
    const token = await getAccessTokenSilently();
    const response = await fetch(`${API_BASE_URL}/progress-tracker/student/${studentId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch student progress');
    }
    return response.json();
  },

  /**
   * Get all progress entries for the logged-in instructor
   */
  getAllProgress: async (getAccessTokenSilently) => {
    const token = await getAccessTokenSilently();
    const response = await fetch(`${API_BASE_URL}/progress-tracker`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch progress entries');
    }
    return response.json();
  }

};

// Payment Collection API
export const paymentAPI = {
  /**
   * Get all payments for the current logged-in instructor
   */
  async getPayments(getAccessTokenSilently) {
    const token = await getAccessTokenSilently();
    const response = await fetch(`${API_BASE_URL}/student-payments`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get payments');
    }

    return response.json();
  },

  /**
   * Create a new payment record
   */
  async createPayment(paymentData, getAccessTokenSilently) {
    const token = await getAccessTokenSilently();
    const response = await fetch(`${API_BASE_URL}/student-payments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(paymentData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create payment');
    }

    return response.json();
  },

  /**
   * Get payment by ID
   */
  async getPaymentById(paymentId, getAccessTokenSilently) {
    const token = await getAccessTokenSilently();

    const response = await fetch(`${API_BASE_URL}/student-payments/${paymentId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get payment');
    }

    return response.json();
  },

  /**
   * Get payments by status (Claimed, Confirmed)
   */
  async getPaymentsByStatus(status, getAccessTokenSilently) {
    const token = await getAccessTokenSilently();

    const response = await fetch(`${API_BASE_URL}/student-payments/status/${status}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get payments by status');
    }

    return response.json();
  },

  /**
   * Get all payments for a student
   */
  async getPaymentsByStudent(studentId, getAccessTokenSilently) {
    const token = await getAccessTokenSilently();

    const response = await fetch(`${API_BASE_URL}/student-payments/student/${studentId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get student payments');
    }

    return response.json();
  },

  /**
   * Get all payments for an instructor
   */
  async getPaymentsByInstructor(instructorId, getAccessTokenSilently) {
    const token = await getAccessTokenSilently();

    const response = await fetch(`${API_BASE_URL}/student-payments/instructor/${instructorId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get instructor payments');
    }

    return response.json();
  },

  /**
   * Approve a payment
   */
  async approvePayment(paymentId, getAccessTokenSilently) {
    const token = await getAccessTokenSilently();

    const response = await fetch(`${API_BASE_URL}/student-payments/${paymentId}/approve`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to approve payment');
    }

    return response.json();
  },

  /**
   * Reject a payment (delete from table)
   */
  async rejectPayment(paymentId, getAccessTokenSilently) {
    const token = await getAccessTokenSilently();

    const response = await fetch(`${API_BASE_URL}/student-payments/${paymentId}/reject`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to reject payment');
    }

    return response.json();
  },

  /**
   * Update payment record
   */
  async updatePayment(paymentId, updates, getAccessTokenSilently) {
    const token = await getAccessTokenSilently();

    const response = await fetch(`${API_BASE_URL}/student-payments/${paymentId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(updates)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update payment');
    }

    return response.json();
  },

  /**
   * Get payment statistics for an instructor
   */
  async getPaymentStats(instructorId, getAccessTokenSilently) {
    const token = await getAccessTokenSilently();

    const response = await fetch(`${API_BASE_URL}/student-payments/stats/${instructorId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get payment stats');
    }

    return response.json();
  },


  /**
   * Get default configuration values
   * @param {Function} getAccessTokenSilently - Auth0 token function
   */
  getDefaults: async (getAccessTokenSilently) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/student-payments/defaults`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch defaults');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching default configuration:', error);
      throw error;
    }
  },

  /**
   * Get all payment records for the authenticated student (ServiceUser)
   */
  async getMyPayments(getAccessTokenSilently) {
    const token = await getAccessTokenSilently();
    const response = await fetch(`${API_BASE_URL}/student-payments/my-payments`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get your payments');
    }

    return response.json();
  }

};